/**
 * The one page layout. Every page renders <Shell> and fills its regions;
 * the partition (header / rail / main / aside / status) and the region sizes
 * live in theme/base.css and tokens.css, not in pages.
 */
import type { ReactNode } from 'react'
import { useRouterState } from '@tanstack/react-router'
import { Header } from './Header'

export interface ShellProps {
  /** Left rail: filters on collections, navigation on entities. Omit for none. */
  rail?: ReactNode
  /** Sticky head inside main (toolbar, identity bar). */
  head?: ReactNode
  children: ReactNode
  /** Right aside: context for the selection. Omit for none. */
  aside?: ReactNode
  /** Status bar content; counts, keys. */
  status?: ReactNode
  railLabel?: string
  asideLabel?: string
  /** Main region fills its height without scrolling (maps, editors). */
  fill?: boolean
}

export function Shell({ rail, head, children, aside, status, railLabel = 'Filters', asideLabel = 'Context', fill }: ShellProps) {
  // Every route change replays the entrance of the regions.
  const pathname = useRouterState({ select: s => s.location.pathname })
  return (
    <div className="shell">
      <div className="shell-header"><Header /></div>
      <div className="shell-body">
        {rail !== undefined ? <aside className="shell-rail page-enter" key={`rail:${pathname}`} aria-label={railLabel}>{rail}</aside> : null}
        <main className="shell-main">
          {head !== undefined ? <div className="main-head page-enter" key={`head:${pathname}`}>{head}</div> : null}
          <div className={fill ? 'shell-main-fill' : 'shell-main-scroll'}><div className={fill ? 'page-enter shell-main-fill' : 'page-enter'} key={`main:${pathname}`}>{children}</div></div>
        </main>
        {aside !== undefined ? <aside className="shell-aside page-enter" key={`aside:${pathname}`} aria-label={asideLabel}>{aside}</aside> : null}
      </div>
      <div className="shell-status"><div className="status">{status ?? <span className="sp" />}</div></div>
    </div>
  )
}

export function StatusKeys({ items }: { items: Array<[string, string]> }) {
  return <>{items.map(([key, label]) => <span key={key + label}><kbd>{key}</kbd>{label}</span>)}</>
}
